import React from 'react';
import { Link } from 'react-router-dom';
import './Legalpages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="legal-last-updated">Last Updated: April 30, 2026</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            FocusView.ai ("we," "our," or "us") operates the website at https://www.focusview.ai (the "Service").
            This Privacy Policy explains how we collect, use, and protect your information. By using our Service,
            you agree to the practices described here. If you do not agree, please discontinue use of the Service.
          </p>
          <p>
            FocusView.ai is operated as a news aggregation and reader-reward platform. We are not affiliated with,
            endorsed by, or sponsored by any news publisher, cryptocurrency project, or financial institution.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>

          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account registration:</strong> Email address and password (hashed, never stored in plain text).</li>
            <li><strong>Dogecoin wallet address:</strong> Optional at registration; required only to initiate a withdrawal. We do not custody your Dogecoin — we only record your wallet address for payout purposes.</li>
            <li><strong>Contact form submissions:</strong> Name, email, and message content when you contact us.</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage data:</strong> Articles viewed, categories browsed, session timestamps.</li>
            <li><strong>Device / browser information:</strong> IP address, browser type, operating system, referring URL.</li>
            <li><strong>Location data:</strong> We use your IP address to estimate your country of residence for eligibility purposes (e.g., withdrawal availability by region). We do not store precise location data beyond country-level.</li>
            <li><strong>Cookies and local storage:</strong> Used for authentication tokens and session management. We do not use third-party tracking cookies beyond Google Analytics (see Section 5).</li>
          </ul>

          <h3>2.3 Information We Do Not Collect</h3>
          <p>
            We do not collect Social Security numbers, government IDs, credit card numbers, or any financial account
            information beyond a Dogecoin wallet address you voluntarily provide.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <ul>
            <li>To create and manage your account.</li>
            <li>To calculate and credit DOGE reading rewards to your account balance.</li>
            <li>To process withdrawal requests by forwarding your wallet address and balance to our administrator.</li>
            <li>To send newsletter emails to subscribers (you can unsubscribe at any time).</li>
            <li>To send transactional emails (account confirmation, password reset).</li>
            <li>To monitor and improve the Service, detect fraud, and ensure security.</li>
            <li>To comply with legal obligations.</li>
          </ul>
          <p>
            We do not sell, rent, or trade your personal information to third parties for marketing purposes.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Dogecoin Rewards — Important Disclosures</h2>
          <p>
            DOGE reading rewards are a <strong>loyalty program</strong>, not an investment, security, or financial
            instrument. Rewards have no guaranteed monetary value and are subject to change or discontinuation at any
            time without notice. Withdrawals are processed manually; we do not guarantee processing times.
            The minimum withdrawal threshold is configurable and may change; the current threshold is displayed in your account dashboard. Rewards are forfeited upon account termination for violations
            of our Terms of Service.
          </p>
          <p>
            We are not a money services business, cryptocurrency exchange, or financial advisor. Nothing on this
            platform constitutes financial advice.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Third-Party Services</h2>
          <ul>
            <li><strong>Google Analytics:</strong> We use Google Analytics to understand aggregate traffic patterns.
              Google may collect your IP address and usage data per their own{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              You can opt out via the{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Add-on</a>.
            </li>
            <li><strong>Resend:</strong> Used to deliver transactional and newsletter emails (account confirmation, OTP codes, withdrawal notifications). Resend processes email addresses for delivery purposes only. See <a href="https://resend.com/privacy" target="_blank" rel="noopener noreferrer">Resend Privacy Policy</a>.</li>
            <li><strong>Anthropic (Claude AI):</strong> Article summaries are generated using Anthropic's API.
              Article content is processed but no personal user data is sent to Anthropic.</li>
            <li><strong>GNews:</strong> News articles are sourced from GNews API. We do not share user data with GNews.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your account data for as long as your account is active. You may delete your account at any time using the <strong>Delete Account</strong> option in the app or website. Account deletion is immediate — your email, wallet address, reward balance, and usage history are permanently removed from our systems at the time of deletion. We send a one-time deletion notification to our administrator for recordkeeping.
            Aggregate, anonymized usage statistics (not linked to you personally) may be retained indefinitely. Financial transaction logs may be retained for up to 7 years as required by applicable law.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Your Rights</h2>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate data.</li>
            <li><strong>Deletion:</strong> Delete your account instantly via the in-app or website Delete Account option. All personal data is removed immediately. No waiting period required.</li>
            <li><strong>Opt-out of newsletters:</strong> Unsubscribe at any time via the link in any newsletter email,
              or by visiting <Link to="/unsubscribe">focusview.ai/unsubscribe</Link>.</li>
            <li><strong>Data portability:</strong> Request an export of your data in a machine-readable format.</li>
          </ul>
          <p>
            To exercise any right, email us at <a href="mailto:admin@focusview.ai">admin@focusview.ai</a>.
            We will respond within 30 days.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. California Residents (CCPA)</h2>
          <p>
            California residents have the right to know what personal information we collect, the right to delete it,
            and the right to opt out of its sale. We do not sell personal information. To exercise your rights,
            contact us at <a href="mailto:admin@focusview.ai">admin@focusview.ai</a>.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Children's Privacy</h2>
          <p>
            Our Service is not directed to children under 18. Account creation and participation in the loyalty rewards program require users to be at least 18 years of age (or the age of majority in their jurisdiction). We do not knowingly collect personal information from minors. If you believe a minor has created an account, contact us at <a href="mailto:admin@focusview.ai">admin@focusview.ai</a> and we will delete the account promptly.
          </p>
        </section>

        <section className="legal-section">
          <h2>9a. Political Content and Editorial Neutrality</h2>
          <p>
            FocusView publishes AI-rephrased summaries of news articles across categories including politics, cryptocurrency, and artificial intelligence. We do not generate original political commentary, endorse candidates, parties, or political positions. Political articles are sourced from established news outlets and presented for informational purposes. We collect no data on users' political views or affiliations.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Security &amp; Fraud Prevention</h2>
          <p>
            We use industry-standard security measures including HTTPS encryption, hashed password storage,
            and access controls. No system is 100% secure; we cannot guarantee absolute security of your data.
          </p>
          <p>
            To protect the integrity of our rewards program we employ the following automated protections:
          </p>
          <ul>
            <li><strong>Rate limiting:</strong> Registration and withdrawal requests are rate-limited per IP address to prevent automated abuse.</li>
            <li><strong>CAPTCHA (Cloudflare Turnstile):</strong> We use Cloudflare Turnstile on registration and withdrawal forms to distinguish humans from bots. Turnstile may collect anonymous interaction signals. Cloudflare's privacy policy applies: <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">cloudflare.com/privacypolicy</a>.</li>
            <li><strong>IP address logging:</strong> We log IP addresses at registration and withdrawal to detect shared-IP abuse and bot activity.</li>
            <li><strong>Behavioral monitoring:</strong> Unusual reading patterns (e.g., reading large numbers of articles in a short period) are flagged for internal review.</li>
            <li><strong>Withdrawal cooldown:</strong> A 30-day cooldown period applies between withdrawal requests to prevent repeated automated submissions.</li>
          </ul>
          <p>
            Flagged accounts may be reviewed by our team. Accounts found to be abusing the rewards system may be suspended without prior notice.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you by updating the "Last Updated"
            date above. Continued use of the Service after changes constitutes your acceptance of the updated policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, contact us at:</p>
          <address>
            FocusView.ai<br />
            Email: <a href="mailto:admin@focusview.ai">admin@focusview.ai</a><br />
            Website: <a href="https://www.focusview.ai">https://www.focusview.ai</a>
          </address>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
